import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TOPICS, TOPIC_LABEL, type Topic } from "@/lib/topics";
import { discoverArticles } from "@/lib/discover.functions";
import type { DiscoveryResult } from "@/lib/discover.functions";
import { discoverVideos } from "@/lib/discover-videos.functions";
import { backfillSelectionReasons } from "@/lib/backfill.functions";
import { fetchArticles, fetchPlaylists, fetchRecipes, fetchVideos } from "@/lib/queries";
import { checkAdminUnlocked, lockAdmin } from "@/lib/admin-gate.functions";
import { getDailyStatus } from "@/lib/status.functions";
import { fetchCurrentWeeklyEssay, submitWeeklyEssay } from "@/lib/weekly-essay.functions";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Redaktion – Flâneriet" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  loader: async () => {
    const { unlocked } = await checkAdminUnlocked();
    if (!unlocked) throw redirect({ to: "/admin/login" });
    return null;
  },
  component: Admin,
});



type Tab = "status" | "opdag" | "opdag-video" | "artikler" | "videoer" | "citater" | "opskrifter" | "playlister";

const TAB_LABELS: Record<Tab, string> = {
  status: "Status",
  opdag: "Opdag artikler",
  "opdag-video": "Opdag videoer",
  artikler: "artikler",
  videoer: "videoer",
  citater: "citater",
  opskrifter: "opskrifter",
  playlister: "playlister",
};

function Admin() {
  const router = useRouter();
  const lock = useServerFn(lockAdmin);
  const [tab, setTab] = useState<Tab>("status");

  async function onLock() {
    await lock({});
    await router.invalidate();
    await router.navigate({ to: "/admin/login" });
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <header className="border-b border-rule pb-6 flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Redaktion</p>
          <h1 className="mt-2 font-serif text-4xl">Redaktørens bord</h1>
          <p className="mt-2 meta italic">Her tilføjes citater, opskrifter og playlister – og herfra lader du systemet gå på opdagelse.</p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <Link
            to="/admin/kurator"
            className="eyebrow text-ink-soft hover:text-bordeaux"
          >
            kurator-lab →
          </Link>
          <button onClick={onLock} className="eyebrow text-ink-soft hover:text-bordeaux">lås</button>
        </div>
      </header>

      <nav className="flex flex-wrap gap-6 border-b border-rule py-4">
        {(["status","opdag","opdag-video","artikler","videoer","citater","opskrifter","playlister"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`eyebrow ${tab === t ? "text-bordeaux" : "hover:text-bordeaux"}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </nav>
      <div className="py-10">
        {tab === "status" && <StatusPanel />}
        {tab === "opdag" && <DiscoverPanel />}
        {tab === "opdag-video" && <DiscoverVideoPanel />}
        {tab === "artikler" && <ArticlesPanel />}
        {tab === "videoer" && <VideosPanel />}
        {tab === "citater" && <QuotesPanel />}
        {tab === "opskrifter" && <RecipesPanel />}
        {tab === "playlister" && <PlaylistsPanel />}
      </div>
    </div>
  );
}


/* ----------------- STATUS ----------------- */
function StatusPanel() {
  const status = useServerFn(getDailyStatus);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-status"],
    queryFn: () => status({ data: { days: 7 } }),
  });

  const days = data?.days ?? [];
  const nf = new Intl.NumberFormat("da-DK");

  return (
    <div className="max-w-4xl">
      <div className="flex items-baseline justify-between gap-6">
        <div>
          <p className="eyebrow">De seneste 7 dage</p>
          <h2 className="mt-2 font-serif text-2xl">Redaktionens døgn</h2>
          <p className="mt-2 meta italic">Hvad systemet har hentet, hvad der er sendt ud, og hvor mange tokens det har kostet.</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} className="eyebrow text-bordeaux disabled:opacity-40">
          {isFetching ? "opdaterer …" : "opdatér"}
        </button>
      </div>

      {isLoading && <p className="mt-8 meta italic">Læser tallene …</p>}
      {error && <p className="mt-8 meta text-destructive">{(error as Error).message}</p>}

      {data?.cron && data.cron.length > 0 && <ScheduleHealth cron={data.cron} />}
      {data?.jobRuns && <JobHistory runs={data.jobRuns} />}
      <WeeklyEssayPanel />





      {days.length > 0 && (
        <div className="mt-8 border-t border-rule">
          {days.map((d) => {
            const dateLabel = new Date(d.date + "T00:00:00Z").toLocaleDateString("da-DK", {
              weekday: "long", day: "numeric", month: "long",
            });
            const isToday = d.date === new Date().toISOString().slice(0, 10);
            const nl = d.newsletter;
            const nlLabel = nl.skipped
              ? "ikke sendt"
              : `${nl.sent} sendt${nl.failed ? ` · ${nl.failed} fejlet` : ""}${nl.suppressed ? ` · ${nl.suppressed} undertrykt` : ""}`;
            const sources = Object.entries(d.ai.bySource).sort((a, b) => b[1] - a[1]);
            return (
              <div key={d.date} className="border-b border-rule py-5">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <p className="font-serif text-lg">
                    {dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}
                    {isToday && <span className="ml-2 eyebrow text-bordeaux">i dag</span>}
                  </p>
                  <p className="meta italic">{nf.format(d.ai.total)} tokens · {d.ai.calls} kald</p>
                </div>
                <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 meta">
                  <Stat label="artikler" value={d.articles} />
                  <Stat label="videoer" value={d.videos} />
                  <Stat label="arrangementer" value={d.events} />
                  <Stat label="dagens ord" value={d.quote ? "ja" : "–"} />
                  <Stat label="hilsen" value={d.greeting ? "ja" : "–"} />
                  <div className="col-span-2 sm:col-span-3">
                    <dt className="eyebrow text-ink-soft">nyhedsbrev</dt>
                    <dd className={`font-serif ${nl.skipped ? "text-ink-soft" : ""}`}>{nlLabel}</dd>
                  </div>
                </dl>
                {sources.length > 0 && (
                  <p className="mt-3 meta text-ink-soft">
                    {sources.map(([s, t]) => `${s}: ${nf.format(t)}`).join(" · ")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <dt className="eyebrow text-ink-soft">{label}</dt>
      <dd className="font-serif">{value}</dd>
    </div>
  );
}

const JOB_LABEL: Record<string, { title: string; when: string }> = {
  "flaneriet-discover-daily": { title: "Artikler, dagens ord & arrangementer", when: "hver morgen kl. 07:00" },
  "flaneriet-discover-videos-daily": { title: "Videoer", when: "hver morgen kl. 08:00" },
  "flaneriet-newsletter-daily": { title: "Nyhedsbrev", when: "hver morgen kl. 10:00" },
  "flaneriet-weekly-essay-friday": { title: "Søndagsbrevet (fredag)", when: "fredag kl. 20:00" },
  "flaneriet-weekly-essay-saturday": { title: "Søndagsbrevet (backup)", when: "lørdag kl. 08:00" },
  "daily-discover": { title: "Artikler, dagens ord & arrangementer", when: "hver morgen" },
  "daily-discover-videos": { title: "Videoer", when: "hver morgen" },
  "daily-newsletter": { title: "Nyhedsbrev", when: "hver morgen" },
};

const TASK_LABEL: Record<string, string> = {
  "discover-articles": "Artikler",
  "quote": "Dagens ord",
  "events": "Arrangementer",
  "discover-videos": "Videoer",
  "newsletter": "Nyhedsbrev",
  "daily-report": "Dagens sundhedstjek",
};


type CronRow = {
  jobname: string; schedule: string; active: boolean;
  last_run: string | null; last_status: string | null; last_message: string | null;
  healthy: boolean;
};

function ScheduleHealth({ cron }: { cron: CronRow[] }) {
  const anyUnhealthy = cron.some((c) => !c.healthy);
  return (
    <section className={`mt-8 border ${anyUnhealthy ? "border-bordeaux" : "border-rule"} p-5`}>
      <div className="flex items-baseline justify-between gap-4">
        <p className="eyebrow">Planlagte kørsler</p>
        {anyUnhealthy && <p className="eyebrow text-bordeaux">noget bør ses efter</p>}
      </div>
      <ul className="mt-4 divide-y divide-rule">
        {cron.map((c) => {
          const meta = JOB_LABEL[c.jobname] ?? { title: c.jobname, when: c.schedule };
          const when = c.last_run
            ? new Date(c.last_run).toLocaleString("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
            : "aldrig";
          const status = !c.active
            ? "slukket"
            : !c.last_run
              ? "endnu ikke kørt"
              : c.healthy
                ? "ok"
                : (c.last_status ?? "sidste kørsel fejlede");
          return (
            <li key={c.jobname} className="py-3 flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <p className="font-serif">{meta.title}</p>
                <p className="meta text-ink-soft italic">{meta.when}</p>
              </div>
              <div className="text-right">
                <p className={`meta ${c.healthy ? "text-ink-soft" : "text-bordeaux"}`}>{status}</p>
                <p className="meta text-ink-soft italic">sidst: {when}</p>
              </div>
            </li>
          );
        })}
      </ul>
      {anyUnhealthy && (
        <p className="mt-4 meta italic text-ink-soft">
          Kør et af de manuelle punkter under fanerne herover, hvis noget mangler for i dag.
        </p>
      )}
    </section>
  );
}

type JobRun = {
  job: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean;
  error_message: string | null;
  result_summary: string | null;
};

function JobHistory({ runs }: { runs: JobRun[] }) {
  const today = new Date().toISOString().slice(0, 10);
  // Seneste kørsel pr. opgave
  const latest = new Map<string, JobRun>();
  for (const r of runs) {
    const prev = latest.get(r.job);
    if (!prev || r.started_at > prev.started_at) latest.set(r.job, r);
  }
  const order = ["discover-articles", "quote", "events", "discover-videos", "newsletter", "daily-report"];
  const items = order
    .map((k) => ({ key: k, run: latest.get(k) }))
    .filter((x) => x.run || TASK_LABEL[x.key]);

  const anyFailed = Array.from(latest.values()).some((r) => !r.ok);

  return (
    <section className={`mt-8 border ${anyFailed ? "border-bordeaux" : "border-rule"} p-5`}>
      <div className="flex items-baseline justify-between gap-4">
        <p className="eyebrow">Seneste opgaver</p>
        {anyFailed && <p className="eyebrow text-bordeaux">én eller flere fejlede</p>}
      </div>
      <ul className="mt-4 divide-y divide-rule">
        {items.map(({ key, run }) => {
          const label = TASK_LABEL[key] ?? key;
          if (!run) {
            return (
              <li key={key} className="py-3 flex items-baseline justify-between gap-4 flex-wrap">
                <p className="font-serif">{label}</p>
                <p className="meta italic text-ink-soft">endnu ikke kørt</p>
              </li>
            );
          }
          const when = new Date(run.started_at).toLocaleString("da-DK", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
          });
          const isToday = run.started_at.slice(0, 10) === today;
          return (
            <li key={key} className="py-3">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="font-serif">
                  {label}
                  {isToday && <span className="ml-2 eyebrow text-ink-soft">i dag</span>}
                </p>
                <p className={`meta ${run.ok ? "text-ink-soft" : "text-bordeaux"}`}>
                  {run.ok ? "ok" : "fejlede"} · {when}
                </p>
              </div>
              {!run.ok && run.error_message && (
                <p className="mt-1 meta text-bordeaux italic break-words">{run.error_message}</p>
              )}
              {run.ok && run.result_summary && (
                <p className="mt-1 meta text-ink-soft italic break-words">{run.result_summary}</p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}



/* ----------------- OPDAGELSE ----------------- */
function DiscoverPanel() {
  const qc = useQueryClient();
  const discover = useServerFn(discoverArticles);
  const [selected, setSelected] = useState<Topic[]>([...TOPICS]);
  const [result, setResult] = useState<DiscoveryResult | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; current: Topic | null }>({ done: 0, total: 0, current: null });

  // Kør ét emne per request, så en enkelt runde aldrig løber ind i workerens
  // ~30-60s timeout (som ellers gav "Load failed" i browseren).
  const run = useMutation({
    mutationFn: async () => {
      const merged: DiscoveryResult = {
        added: [], rejected: [], errors: [],
        diagnostics: {
          dailyTarget: 4,
          existingToday: 0,
          targetForRun: 0,
          daySince: "",
          searches: 0,
          broadSearches: 0,
          rawHits: 0,
          candidatesForAi: 0,
          alreadyKnown: 0,
          duplicateCandidates: 0,
          rejectedBeforeAi: 0,
          rejectedByAi: 0,
          unacceptableUrl: 0,
          tooShort: 0,
          needsAfterRun: 0,
        },
        languageBalance: {
          da: 0,
          en: 0,
          removedForBalance: [],
          note: "Dansk prioriteres, men engelske kvalitetsfund slettes ikke længere.",
        },
      };
      setProgress({ done: 0, total: selected.length, current: selected[0] ?? null });
      for (let i = 0; i < selected.length; i++) {
        const topic = selected[i];
        setProgress({ done: i, total: selected.length, current: topic });
        try {
          const r = await discover({ data: { topics: [topic], perTopic: 3 } }) as DiscoveryResult;
          merged.added.push(...r.added);
          merged.rejected.push(...r.rejected);
          merged.errors.push(...r.errors);
          if (merged.diagnostics && r.diagnostics) {
            merged.diagnostics.dailyTarget = r.diagnostics.dailyTarget;
            merged.diagnostics.existingToday = Math.max(merged.diagnostics.existingToday, r.diagnostics.existingToday);
            merged.diagnostics.targetForRun += r.diagnostics.targetForRun;
            merged.diagnostics.daySince = r.diagnostics.daySince;
            merged.diagnostics.searches += r.diagnostics.searches;
            merged.diagnostics.broadSearches += r.diagnostics.broadSearches;
            merged.diagnostics.rawHits += r.diagnostics.rawHits;
            merged.diagnostics.candidatesForAi += r.diagnostics.candidatesForAi;
            merged.diagnostics.alreadyKnown += r.diagnostics.alreadyKnown;
            merged.diagnostics.duplicateCandidates += r.diagnostics.duplicateCandidates;
            merged.diagnostics.rejectedBeforeAi += r.diagnostics.rejectedBeforeAi;
            merged.diagnostics.rejectedByAi += r.diagnostics.rejectedByAi;
            merged.diagnostics.unacceptableUrl += r.diagnostics.unacceptableUrl;
            merged.diagnostics.tooShort += r.diagnostics.tooShort;
            merged.diagnostics.accepted = (merged.diagnostics.accepted ?? 0) + (r.diagnostics.accepted ?? 0);
            merged.diagnostics.needsAfterRun = r.diagnostics.needsAfterRun;
          }
          merged.languageBalance.da += r.languageBalance?.da ?? 0;
          merged.languageBalance.en += r.languageBalance?.en ?? 0;
          merged.languageBalance.englishFallbackUsed ||= !!r.languageBalance?.englishFallbackUsed;
          if (r.languageBalance?.removedForBalance?.length) {
            merged.languageBalance.removedForBalance.push(...r.languageBalance.removedForBalance);
          }
        } catch (e) {
          merged.errors.push(`${topic}: ${(e as Error).message}`);
        }
      }
      setProgress({ done: selected.length, total: selected.length, current: null });
      return merged;
    },
    onSuccess: (r) => { setResult(r); qc.invalidateQueries(); },
  });

  return (
    <div className="max-w-2xl">
      <p className="text-lg leading-relaxed">
        Systemet søger essayistisk kulturstof fra nettet inden for de valgte emner, lader en AI-redaktør bedømme hver artikel efter Flâneriets kriterier, og gemmer kun det, der kvalificerer – aldrig hastighed, buzzwords eller optimering.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        {TOPICS.map((t) => {
          const on = selected.includes(t);
          return (
            <button
              key={t}
              onClick={() => setSelected((s) => on ? s.filter((x) => x !== t) : [...s, t])}
              className={`eyebrow border px-3 py-1.5 transition-colors duration-500 ${on ? "border-bordeaux text-bordeaux" : "border-rule text-ink-soft hover:text-bordeaux"}`}
            >
              {TOPIC_LABEL[t]}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => run.mutate()}
        disabled={run.isPending || selected.length === 0}
        className="mt-8 font-serif text-lg text-bordeaux border-b border-bordeaux pb-1 disabled:opacity-40"
      >
        {run.isPending ? "Redaktøren læser …" : "Gå på opdagelse nu"}
      </button>

      <BackfillReasonsButton />


      {run.isPending && (
        <p className="mt-4 meta italic">
          Læser {progress.current ? TOPIC_LABEL[progress.current] : "…"} ({progress.done + 1}/{progress.total}). Ét emne ad gangen – hold linjen.
        </p>
      )}
      {run.isError && <p className="mt-4 meta text-destructive">{(run.error as Error).message}</p>}

      {result && (
        <div className="mt-10 border-t border-rule pt-6 space-y-6">
          {result.diagnostics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-rule pb-6">
              <div>
                <p className="eyebrow">Døgnmål</p>
                <p className="mt-1 font-serif text-2xl">{result.diagnostics.dailyTarget}</p>
                <p className="meta italic text-ink-soft">mangler {result.diagnostics.needsAfterRun}</p>
              </div>
              <div>
                <p className="eyebrow">Søgninger</p>
                <p className="mt-1 font-serif text-2xl">{result.diagnostics.searches}</p>
                <p className="meta italic text-ink-soft">brede {result.diagnostics.broadSearches}</p>
              </div>
              <div>
                <p className="eyebrow">Kandidater</p>
                <p className="mt-1 font-serif text-2xl">{result.diagnostics.candidatesForAi}</p>
                <p className="meta italic text-ink-soft">rå fund {result.diagnostics.rawHits}</p>
              </div>
              <div>
                <p className="eyebrow">Afvist før AI</p>
                <p className="mt-1 font-serif text-2xl">{result.diagnostics.rejectedBeforeAi}</p>
                <p className="meta italic text-ink-soft">AI afviste {result.diagnostics.rejectedByAi}</p>
              </div>
            </div>
          )}
          <div>
            <p className="eyebrow">Optaget ({result.added.length})</p>
            {result.added.length === 0 && <p className="meta italic mt-2">Intet nyt at berette – kom tilbage senere.</p>}
            <ul className="mt-3 divide-y divide-rule">
              {result.added.map((a, i) => (
                <li key={i} className="py-2 flex justify-between gap-4 flex-wrap">
                  <span className="font-serif">{a.title}</span>
                  <span className="meta italic">{a.source} · {TOPIC_LABEL[a.topic]}</span>
                </li>
              ))}
            </ul>
          </div>
          {result.rejected.length > 0 && (
            <div>
              <p className="eyebrow">Afvist ({result.rejected.length})</p>
              <ul className="mt-3 space-y-2">
                {result.rejected.slice(0, 8).map((r, i) => (
                  <li key={i} className="meta"><span className="italic">{r.title}</span> – {r.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {result.errors.length > 0 && (
            <div>
              <p className="eyebrow text-destructive">Fejl</p>
              <ul className="mt-2 space-y-1">{result.errors.map((e, i) => <li key={i} className="meta">{e}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BackfillReasonsButton() {
  const qc = useQueryClient();
  const backfill = useServerFn(backfillSelectionReasons);
  const [result, setResult] = useState<{ updated: string[]; errors: string[] } | null>(null);
  const run = useMutation({
    mutationFn: () => backfill({ data: { limit: 5 } }),
    onSuccess: (r) => { setResult(r); qc.invalidateQueries(); },
  });
  return (
    <div className="mt-10 border-t border-rule pt-6">
      <p className="eyebrow">Redaktørens noter</p>
      <p className="mt-2 meta italic">Berig de 5 seneste artikler uden en “Derfor”-note. Fremtidige artikler får noten automatisk.</p>
      <button
        onClick={() => run.mutate()}
        disabled={run.isPending}
        className="mt-4 font-serif text-base text-bordeaux border-b border-bordeaux pb-1 disabled:opacity-40"
      >
        {run.isPending ? "Skriver noter …" : "Berig de seneste 5"}
      </button>
      {run.isError && <p className="mt-3 meta text-destructive">{(run.error as Error).message}</p>}
      {result && (
        <div className="mt-4 space-y-2">
          <p className="meta">Beriget: {result.updated.length}</p>
          {result.updated.length > 0 && (
            <ul className="mt-1 space-y-1">
              {result.updated.map((t, i) => <li key={i} className="meta italic">{t}</li>)}
            </ul>
          )}
          {result.errors.length > 0 && (
            <ul className="mt-2 space-y-1">
              {result.errors.map((e, i) => <li key={i} className="meta text-destructive">{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ----------------- OPDAGELSE (VIDEO) ----------------- */
function DiscoverVideoPanel() {
  const qc = useQueryClient();
  const discover = useServerFn(discoverVideos);
  const [selected, setSelected] = useState<Topic[]>([...TOPICS]);
  const [result, setResult] = useState<Awaited<ReturnType<typeof discover>> | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; current: Topic | null }>({ done: 0, total: 0, current: null });

  const run = useMutation({
    mutationFn: async () => {
      const merged: Awaited<ReturnType<typeof discover>> = { added: [], rejected: [], errors: [], purged: 0 };
      setProgress({ done: 0, total: selected.length, current: selected[0] ?? null });
      for (let i = 0; i < selected.length; i++) {
        const topic = selected[i];
        setProgress({ done: i, total: selected.length, current: topic });
        try {
          const r = await discover({ data: { topics: [topic], perTopic: 2 } });
          merged.added.push(...r.added);
          merged.rejected.push(...r.rejected);
          merged.errors.push(...r.errors);
          merged.purged = (merged.purged ?? 0) + (r.purged ?? 0);
        } catch (e) {
          merged.errors.push(`${topic}: ${(e as Error).message}`);
        }
      }
      setProgress({ done: selected.length, total: selected.length, current: null });
      return merged;
    },
    onSuccess: (r) => { setResult(r); qc.invalidateQueries(); },
  });

  return (
    <div className="max-w-2xl">
      <p className="text-lg leading-relaxed">
        Systemet søger YouTube- og Vimeo-videoer indenfor de valgte emner – portrætter, dokumentarer, koncerter, oplæsninger – og lader en AI-redaktør frasortere alt hvad der er tips, hacks, listicles eller clickbait.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        {TOPICS.map((t) => {
          const on = selected.includes(t);
          return (
            <button
              key={t}
              onClick={() => setSelected((s) => on ? s.filter((x) => x !== t) : [...s, t])}
              className={`eyebrow border px-3 py-1.5 transition-colors duration-500 ${on ? "border-bordeaux text-bordeaux" : "border-rule text-ink-soft hover:text-bordeaux"}`}
            >
              {TOPIC_LABEL[t]}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => run.mutate()}
        disabled={run.isPending || selected.length === 0}
        className="mt-8 font-serif text-lg text-bordeaux border-b border-bordeaux pb-1 disabled:opacity-40"
      >
        {run.isPending ? "Redaktøren ser efter …" : "Gå på opdagelse nu"}
      </button>

      {run.isPending && (
        <p className="mt-4 meta italic">
          Ser {progress.current ? TOPIC_LABEL[progress.current] : "…"} efter ({progress.done + 1}/{progress.total}). Ét emne ad gangen.
        </p>
      )}
      {run.isError && <p className="mt-4 meta text-destructive">{(run.error as Error).message}</p>}

      {result && (
        <div className="mt-10 border-t border-rule pt-6 space-y-6">
          <div>
            <p className="eyebrow">Optaget ({result.added.length})</p>
            {result.added.length === 0 && <p className="meta italic mt-2">Intet nyt at berette – kom tilbage senere.</p>}
            <ul className="mt-3 divide-y divide-rule">
              {result.added.map((a, i) => (
                <li key={i} className="py-2 flex justify-between gap-4 flex-wrap">
                  <span className="font-serif">{a.title}</span>
                  <span className="meta italic">
                    {a.channel ?? a.provider} · {TOPIC_LABEL[a.topic]}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {result.rejected.length > 0 && (
            <div>
              <p className="eyebrow">Afvist ({result.rejected.length})</p>
              <ul className="mt-3 space-y-2">
                {result.rejected.slice(0, 8).map((r, i) => (
                  <li key={i} className="meta"><span className="italic">{r.title}</span> – {r.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {result.errors.length > 0 && (
            <div>
              <p className="eyebrow text-destructive">Fejl</p>
              <ul className="mt-2 space-y-1">{result.errors.map((e, i) => <li key={i} className="meta">{e}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ----------------- VIDEOER ----------------- */
function VideosPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-videos"], queryFn: () => fetchVideos() });

  async function remove(id: string) {
    if (!confirm("Fjern videoen?")) return;
    await supabase.from("videos").delete().eq("id", id);
    qc.invalidateQueries();
  }

  return (
    <div>
      <p className="eyebrow">I biblioteket ({data?.length ?? 0})</p>
      <p className="meta italic mt-1">Nye videoer optages via "Opdag videoer". Her kan de fjernes.</p>
      <ul className="mt-4 divide-y divide-rule max-h-[700px] overflow-y-auto pr-2">
        {data?.map((v) => (
          <li key={v.id} className="py-3 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <p className="font-serif text-lg">{v.title}</p>
              <p className="meta truncate">
                {v.provider === "youtube" ? "YouTube" : "Vimeo"}
                {v.channel ? ` · ${v.channel}` : ""} · {TOPIC_LABEL[v.topic]}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <a
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                className="meta text-bordeaux"
              >
                se
              </a>
              <button onClick={() => remove(v.id)} className="meta text-destructive">fjern</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


/* ----------------- ARTIKLER ----------------- */
function ArticlesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-articles"], queryFn: () => fetchArticles() });
  const [form, setForm] = useState({
    title: "", source: "", original_url: "", topic: "kultur" as Topic,
    language: "da", paywall: false, summary: "", image_url: "", published_date: "",
  });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const { error } = await supabase.from("articles").insert({
      title: form.title, source: form.source, original_url: form.original_url,
      topic: form.topic as never, language: form.language, paywall: form.paywall,
      summary: form.summary, image_url: form.image_url || null,
      published_date: form.published_date || null,
    });
    if (error) return alert(error.message);
    setForm({ ...form, title: "", original_url: "", summary: "", image_url: "" });
    qc.invalidateQueries();
  }

  async function remove(id: string) {
    if (!confirm("Fjern artiklen?")) return;
    await supabase.from("articles").delete().eq("id", id);
    qc.invalidateQueries();
  }

  return (
    <div className="grid gap-12 lg:grid-cols-2">
      <form onSubmit={save} className="space-y-4">
        <p className="eyebrow">Ny artikel</p>
        <Field label="Titel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
        <Field label="Kilde (medie)" value={form.source} onChange={(v) => setForm({ ...form, source: v })} required />
        <Field label="URL til kilden" value={form.original_url} onChange={(v) => setForm({ ...form, original_url: v })} required type="url" />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Emne" value={form.topic} onChange={(v) => setForm({ ...form, topic: v as Topic })}
            options={TOPICS.map((t) => ({ value: t, label: TOPIC_LABEL[t] }))} />
          <Select label="Sprog" value={form.language} onChange={(v) => setForm({ ...form, language: v })}
            options={[{ value: "da", label: "Dansk" }, { value: "en", label: "Engelsk" }]} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Dato" type="date" value={form.published_date} onChange={(v) => setForm({ ...form, published_date: v })} />
          <label className="meta inline-flex items-center gap-2 mt-6 select-none">
            <input type="checkbox" className="accent-bordeaux" checked={form.paywall} onChange={(e) => setForm({ ...form, paywall: e.target.checked })} />
            Bag betalingsmur
          </label>
        </div>
        <Field label="Billed-URL (valgfri)" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
        <TextArea label="Resumé (2-3 sætninger, egen stemme)" value={form.summary} onChange={(v) => setForm({ ...form, summary: v })} required rows={4} />
        <button className="font-serif text-lg text-bordeaux border-b border-bordeaux pb-1">Gem artikel</button>
      </form>

      <div>
        <p className="eyebrow">I biblioteket ({data?.length ?? 0})</p>
        <ul className="mt-4 divide-y divide-rule max-h-[600px] overflow-y-auto pr-2">
          {data?.map((a) => (
            <li key={a.id} className="py-3">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <p className="font-serif text-lg">{a.title}</p>
                  <p className="meta">{a.source} · {TOPIC_LABEL[a.topic as Topic]} · {a.paywall ? "betaling" : "fri"}</p>
                </div>
                <button onClick={() => remove(a.id)} className="meta text-destructive">fjern</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ----------------- CITATER ----------------- */
function QuotesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin-quotes"],
    queryFn: async () => (await supabase.from("quotes").select("*").order("created_at", { ascending: false })).data ?? [],
  });
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("");

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    await supabase.from("quotes").insert({ text, author: author || null });
    setText(""); setAuthor("");
    qc.invalidateQueries();
  }
  async function toggle(id: string, active: boolean) {
    await supabase.from("quotes").update({ active: !active }).eq("id", id);
    qc.invalidateQueries();
  }
  async function remove(id: string) {
    if (!confirm("Fjern citatet?")) return;
    await supabase.from("quotes").delete().eq("id", id);
    qc.invalidateQueries();
  }

  return (
    <div className="grid gap-12 lg:grid-cols-2">
      <form onSubmit={add} className="space-y-4">
        <p className="eyebrow">Nyt citat</p>
        <TextArea label="Citatet" value={text} onChange={setText} rows={6} required />
        <Field label="Forfatter (valgfri)" value={author} onChange={setAuthor} />
        <button className="font-serif text-lg text-bordeaux border-b border-bordeaux pb-1">Føj til samlingen</button>
      </form>
      <div>
        <p className="eyebrow">Samlingen ({data?.length ?? 0})</p>
        <ul className="mt-4 space-y-4 max-h-[600px] overflow-y-auto pr-2">
          {data?.map((q) => (
            <li key={q.id} className="border-b border-rule pb-4">
              <p className="font-serif italic leading-relaxed">{"“"}{q.text}{"”"}</p>
              {q.author && <p className="meta mt-2">– {q.author}</p>}
              <div className="mt-2 flex gap-4">
                <button onClick={() => toggle(q.id, q.active)} className="meta text-bordeaux">{q.active ? "aktiv" : "hvile"}</button>
                <button onClick={() => remove(q.id)} className="meta text-destructive">fjern</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ----------------- OPSKRIFTER ----------------- */
function RecipesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-recipes"], queryFn: fetchRecipes });
  const [f, setF] = useState({
    title: "", chef: "", source: "", original_url: "", summary: "",
    ingredients: "", method: "", image_url: "", paywall: false,
  });
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const { error } = await supabase.from("recipes").insert({
      ...f, image_url: f.image_url || null, source: f.source || null,
      original_url: f.original_url || null,
    });
    if (error) return alert(error.message);
    setF({ title: "", chef: "", source: "", original_url: "", summary: "", ingredients: "", method: "", image_url: "", paywall: false });
    qc.invalidateQueries();
  }
  async function remove(id: string) {
    if (!confirm("Fjern opskriften?")) return;
    await supabase.from("recipes").delete().eq("id", id);
    qc.invalidateQueries();
  }
  return (
    <div className="grid gap-12 lg:grid-cols-2">
      <form onSubmit={save} className="space-y-4">
        <p className="eyebrow">Ny opskrift</p>
        <Field label="Titel" value={f.title} onChange={(v) => setF({ ...f, title: v })} required />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Kok" value={f.chef} onChange={(v) => setF({ ...f, chef: v })} />
          <Field label="Kilde" value={f.source} onChange={(v) => setF({ ...f, source: v })} />
        </div>
        <Field label="URL (valgfri)" value={f.original_url} onChange={(v) => setF({ ...f, original_url: v })} />
        <Field label="Billed-URL (valgfri)" value={f.image_url} onChange={(v) => setF({ ...f, image_url: v })} />
        <TextArea label="Kort introduktion" value={f.summary} onChange={(v) => setF({ ...f, summary: v })} rows={3} required />
        <TextArea label="Ingredienser (én pr. linje)" value={f.ingredients} onChange={(v) => setF({ ...f, ingredients: v })} rows={6} />
        <TextArea label="Fremgangsmåde" value={f.method} onChange={(v) => setF({ ...f, method: v })} rows={8} />
        <label className="meta inline-flex items-center gap-2">
          <input type="checkbox" className="accent-bordeaux" checked={f.paywall} onChange={(e) => setF({ ...f, paywall: e.target.checked })} />
          Bag betalingsmur
        </label>
        <div><button className="font-serif text-lg text-bordeaux border-b border-bordeaux pb-1">Gem opskrift</button></div>
      </form>
      <div>
        <p className="eyebrow">Køkkenets bibliotek ({data?.length ?? 0})</p>
        <ul className="mt-4 divide-y divide-rule">
          {data?.map((r) => (
            <li key={r.id} className="py-3 flex justify-between gap-3">
              <div>
                <p className="font-serif text-lg">{r.title}</p>
                <p className="meta">{r.chef ?? ""}{r.source ? ` · ${r.source}` : ""}</p>
              </div>
              <button onClick={() => remove(r.id)} className="meta text-destructive">fjern</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ----------------- PLAYLISTER ----------------- */
function PlaylistsPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-playlists"], queryFn: fetchPlaylists });
  const [f, setF] = useState({ title: "", description: "", mood: "Jazz", spotify_embed_url: "", image_url: "" });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    let url = f.spotify_embed_url.trim();
    // Aksepter både share-links og embed-links.
    const m = url.match(/open\.spotify\.com\/(?:embed\/)?(playlist|album|track|artist)\/([a-zA-Z0-9]+)/);
    if (m) url = `https://open.spotify.com/embed/${m[1]}/${m[2]}`;
    const { error } = await supabase.from("playlists").insert({
      title: f.title, description: f.description || null, mood: f.mood || null,
      spotify_embed_url: url, image_url: f.image_url || null,
    });
    if (error) return alert(error.message);
    setF({ title: "", description: "", mood: "Jazz", spotify_embed_url: "", image_url: "" });
    qc.invalidateQueries();
  }
  async function remove(id: string) {
    if (!confirm("Fjern playlisten?")) return;
    await supabase.from("playlists").delete().eq("id", id);
    qc.invalidateQueries();
  }
  return (
    <div className="grid gap-12 lg:grid-cols-2">
      <form onSubmit={save} className="space-y-4">
        <p className="eyebrow">Ny playliste</p>
        <Field label="Titel" value={f.title} onChange={(v) => setF({ ...f, title: v })} required />
        <Field label="Stemning" value={f.mood} onChange={(v) => setF({ ...f, mood: v })} />
        <TextArea label="Beskrivelse" value={f.description} onChange={(v) => setF({ ...f, description: v })} rows={2} />
        <Field label="Spotify-link (share eller embed)" value={f.spotify_embed_url} onChange={(v) => setF({ ...f, spotify_embed_url: v })} required />
        <p className="meta italic">Kopier fra Spotify: højreklik → Del → Kopiér link.</p>
        <div><button className="font-serif text-lg text-bordeaux border-b border-bordeaux pb-1">Gem playliste</button></div>
      </form>
      <div>
        <p className="eyebrow">Stemninger ({data?.length ?? 0})</p>
        <ul className="mt-4 divide-y divide-rule">
          {data?.map((p) => (
            <li key={p.id} className="py-3 flex justify-between gap-3">
              <div>
                <p className="font-serif text-lg">{p.title}</p>
                <p className="meta">{p.mood}</p>
              </div>
              <button onClick={() => remove(p.id)} className="meta text-destructive">fjern</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ----------------- FIELDS ----------------- */
function Field({ label, value, onChange, type = "text", required }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type={type} required={required} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border-b border-rule bg-transparent py-2 font-serif text-lg outline-none focus:border-bordeaux transition-colors"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 4, required }: { label: string; value: string; onChange: (v: string) => void; rows?: number; required?: boolean }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <textarea
        rows={rows} required={required} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-rule bg-transparent p-3 font-serif text-lg leading-relaxed outline-none focus:border-bordeaux transition-colors resize-y"
      />
    </label>
  );
}
function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border-b border-rule bg-transparent py-2 font-serif text-lg outline-none focus:border-bordeaux"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

/* ----------------- SØNDAGSBREVET (redaktørens nødhjul) ----------------- */
function WeeklyEssayPanel() {
  const fetchCurrent = useServerFn(fetchCurrentWeeklyEssay);
  const submit = useServerFn(submitWeeklyEssay);
  const qc = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: ["weekly-essay-current"],
    queryFn: () => fetchCurrent({}),
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    source_url: "",
    title: "",
    author: "",
    source_name: "",
    excerpt: "",
  });
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: (input: typeof form) => submit({ data: input }),
    onSuccess: () => {
      setOpen(false);
      setForm({ source_url: "", title: "", author: "", source_name: "", excerpt: "" });
      setErr(null);
      qc.invalidateQueries({ queryKey: ["weekly-essay-current"] });
      refetch();
    },
    onError: (e: Error) => setErr(e.message),
  });

  const essay = data?.essay;
  const week = data?.week_start;
  const hasEssay = essay && essay.title;

  return (
    <section className="mt-8 border border-rule p-5">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Søndagsbrevet</p>
          <p className="meta text-ink-soft italic">
            uge fra {week ?? "–"} · agenten leder fredag aften og lørdag morgen
          </p>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="eyebrow text-bordeaux hover:underline"
        >
          {open ? "annullér" : hasEssay ? "erstat manuelt" : "indsend manuelt"}
        </button>
      </div>

      {hasEssay ? (
        <div className="mt-4">
          <p className="font-serif text-lg">{essay.title}</p>
          <p className="meta text-ink-soft italic">
            {essay.author ?? "ukendt forfatter"} · {essay.source_name ?? "ukendt kilde"}
            {essay.manually_curated ? " · indsendt manuelt" : " · fundet af agenten"}
          </p>
          {essay.source_url && (
            <a
              href={essay.source_url}
              target="_blank"
              rel="noreferrer"
              className="meta text-bordeaux hover:underline"
            >
              se kilden
            </a>
          )}
        </div>
      ) : (
        <p className="mt-4 meta italic text-ink-soft">
          Intet søndagsbrev fundet endnu for denne uge. Se seneste kørsler for hvorfor,
          eller indsend et manuelt.
        </p>
      )}

      {open && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErr(null);
            mut.mutate(form);
          }}
          className="mt-6 grid gap-4 border-t border-rule pt-6"
        >
          <label className="grid gap-1">
            <span className="eyebrow">kilde-URL</span>
            <input
              type="url"
              required
              value={form.source_url}
              onChange={(e) => setForm((f) => ({ ...f, source_url: e.target.value }))}
              className="border border-rule px-3 py-2 font-serif"
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1">
              <span className="eyebrow">titel</span>
              <input
                required
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="border border-rule px-3 py-2 font-serif"
              />
            </label>
            <label className="grid gap-1">
              <span className="eyebrow">forfatter</span>
              <input
                required
                value={form.author}
                onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                className="border border-rule px-3 py-2 font-serif"
              />
            </label>
          </div>
          <label className="grid gap-1">
            <span className="eyebrow">kildenavn (fx Weekendavisen)</span>
            <input
              required
              value={form.source_name}
              onChange={(e) => setForm((f) => ({ ...f, source_name: e.target.value }))}
              className="border border-rule px-3 py-2 font-serif"
            />
          </label>
          <label className="grid gap-1">
            <span className="eyebrow">uddrag (~300–500 ord)</span>
            <textarea
              required
              rows={10}
              value={form.excerpt}
              onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
              className="border border-rule px-3 py-2 font-serif"
            />
          </label>
          {err && <p className="meta text-destructive">{err}</p>}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={mut.isPending}
              className="eyebrow text-bordeaux disabled:opacity-40"
            >
              {mut.isPending ? "gemmer …" : "gem som ugens søndagsbrev"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
